import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { BottomTabBar } from '@/components/layout/BottomTabBar'
import { formatShortThaiDate } from '@krudream/shared'
import { apiUrl } from '@/lib/api'

interface Homework {
  id: string
  subject: string
  title: string
  dueDate: string
  submittedCount: number
  totalStudents: number
  pendingCount: number
  isOverdue: boolean
}

type FilterTab = 'active' | 'all' | 'overdue'

export function HomeworkListPage() {
  const navigate = useNavigate()
  const [homeworks, setHomeworks] = useState<Homework[]>([])
  const [filter, setFilter] = useState<FilterTab>('active')
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    fetchHomework()
  }, [filter])

  async function fetchHomework() {
    setIsLoading(true)
    try {
      const res = await fetch(apiUrl(`/api/homework?status=${filter}`), { credentials: 'include' })
      const data = await res.json()
      if (data.success) setHomeworks(data.data)
    } catch {}
    setIsLoading(false)
  }

  const subjectIcons: Record<string, string> = {
    'คณิตศาสตร์': '📐',
    'ภาษาไทย': '📖',
    'วิทยาศาสตร์': '🔬',
    'สังคมศึกษา': '🌏',
    'ภาษาอังกฤษ': '🔤',
    'ศิลปะ': '🎨',
    'สุขศึกษา': '💪',
    'การงานอาชีพ': '🔧',
  }

  function formatDueDate(dateStr: string): string {
    const today = new Date().toISOString().split('T')[0]
    const tomorrow = new Date(Date.now() + 86400000).toISOString().split('T')[0]
    if (dateStr === today) return 'วันนี้'
    if (dateStr === tomorrow) return 'พรุ่งนี้'
    if (dateStr < today) return 'เลยกำหนด'
    return formatShortThaiDate(new Date(dateStr + 'T00:00:00'))
  }

  const tabs: { key: FilterTab; label: string }[] = [
    { key: 'active', label: 'กำลังดำเนินการ' },
    { key: 'all', label: 'ทั้งหมด' },
    { key: 'overdue', label: 'เลยกำหนด' },
  ]

  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      <header className="bg-white px-4 py-3 shadow-sm">
        <div className="mx-auto flex max-w-lg items-center justify-between">
          <h1 className="text-lg font-bold text-gray-900">การบ้าน</h1>
          <button
            onClick={() => navigate('/homework/new')}
            className="text-xl text-primary-600"
          >
            ⊕
          </button>
        </div>
      </header>

      <main className="mx-auto max-w-lg px-4 py-3">
        {/* Filter tabs */}
        <div className="mb-3 flex gap-2">
          {tabs.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setFilter(tab.key)}
              className={`rounded-full px-3 py-1 text-xs font-medium transition-colors ${
                filter === tab.key
                  ? 'bg-primary-600 text-white'
                  : 'bg-white text-gray-600 shadow-sm'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {isLoading ? (
          <div className="text-center text-sm text-gray-400">กำลังโหลด...</div>
        ) : homeworks.length === 0 ? (
          <div className="rounded-lg bg-white p-8 text-center text-sm text-gray-400 shadow-sm">
            ไม่มีการบ้าน
          </div>
        ) : (
          <div className="space-y-3">
            {homeworks.map((hw) => {
              const pct = hw.totalStudents > 0
                ? Math.round((hw.submittedCount / hw.totalStudents) * 100)
                : 0

              return (
                <button
                  key={hw.id}
                  onClick={() => navigate(`/homework/${hw.id}`)}
                  className="block w-full rounded-lg bg-white p-4 text-left shadow-sm transition-colors hover:bg-gray-50"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-2">
                      <span>{subjectIcons[hw.subject] || '📚'}</span>
                      <span className="text-sm font-medium text-gray-900">{hw.subject}</span>
                    </div>
                    {hw.isOverdue && (
                      <span className="rounded bg-red-50 px-1.5 py-0.5 text-xs font-medium text-red-600">
                        ! เลยกำหนด
                      </span>
                    )}
                  </div>
                  <p className="mt-1 text-sm text-gray-700">{hw.title}</p>
                  <p className="mt-1 text-xs text-gray-400">
                    ส่ง: {formatDueDate(hw.dueDate)} · ส่งแล้ว {hw.submittedCount}/{hw.totalStudents}
                  </p>
                  {/* Progress bar */}
                  <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-gray-100">
                    <div
                      className={`h-full rounded-full transition-all ${
                        hw.isOverdue ? 'bg-red-400' : 'bg-primary-500'
                      }`}
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                </button>
              )
            })}
          </div>
        )}
      </main>

      <BottomTabBar active="homework" />
    </div>
  )
}
